/* eslint-disable tailwindcss/no-arbitrary-value */
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { TheWrapper } from '@hyphengroup/cms-components/components/TheWrapper';
import classNames from 'classnames';
import React from 'react';
import { ARTICLE_IMAGE_SIZES } from '../helpers/articleImage';
import {
  ComponentImage,
  EnumImageAlignment,
  EnumImageWidth,
  PageType,
} from '$/(cms)/_modules/cmsService/types';

const getAlignmentClass = (
  alignment: EnumImageAlignment | undefined,
  isFullWidth: boolean,
) => {
  if (isFullWidth) return '';
  if (alignment === EnumImageAlignment.Center) return 'md:mx-auto';
  if (alignment === EnumImageAlignment.Right) return 'md:ml-auto';
  return 'md:mr-auto';
};

const getCaptionAlignmentClass = (
  alignment: EnumImageAlignment | undefined,
  isFullWidth: boolean,
) => {
  if (isFullWidth) return 'text-left';
  if (alignment === EnumImageAlignment.Left) return 'text-left';
  if (alignment === EnumImageAlignment.Right) return 'text-right';
  return 'text-center';
};

const getWrapperMaxWidthClass = (width: EnumImageWidth | undefined) => {
  if (width === EnumImageWidth.Width_20Percent) return 'md:max-w-[20%]';
  if (width === EnumImageWidth.Width_50Percent) return 'md:max-w-[50%]';
  if (width === EnumImageWidth.Width_100Percent) return 'md:max-w-full';
  return 'md:max-w-[70%]';
};

const getSizes = (width: EnumImageWidth | undefined) => {
  switch (width) {
    case EnumImageWidth.Width_20Percent:
      return { _: '100vw', md: '20vw' };
    case EnumImageWidth.Width_50Percent:
      return { _: '100vw', md: '50vw' };
    case EnumImageWidth.Width_100Percent:
      return { _: '100vw' };
    default:
      return { _: '100vw', md: '70vw' };
  }
};

export const ImageCmp: React.FC<ComponentImage & { pageType?: PageType }> = ({
  image,
  caption,
  visible,
  width,
  alignment,
  pageType,
}) => {
  if (visible === false || !image?.url) {
    return null;
  }

  const isFullWidth = width === EnumImageWidth.Width_100Percent;
  const isArticleFullWidthImage = pageType === 'article' && isFullWidth;

  const alignmentClass = getAlignmentClass(alignment, isArticleFullWidthImage);
  const captionAlignmentClass = getCaptionAlignmentClass(
    alignment,
    isArticleFullWidthImage,
  );

  return (
    <TheWrapper type="simple">
      <div
        className={classNames(
          `w-full max-w-full ${getWrapperMaxWidthClass(width)}`,
          alignmentClass,
        )}
      >
        {isArticleFullWidthImage ? (
          <div className="relative aspect-[5/2] w-full overflow-hidden rounded-2xl xl:rounded-3xl">
            <CmsImage
              src={image.url}
              alt={image.alternativeText ?? ''}
              fill
              sizes={ARTICLE_IMAGE_SIZES}
              className="object-cover object-center"
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : (
          <CmsImage
            alt={image?.alternativeText ?? ''}
            src={image.url}
            width={image.width ?? 100}
            height={image.height ?? 100}
            sizes={getSizes(width)}
            className="h-auto w-full"
            loading="lazy"
            decoding="async"
          />
        )}
        {caption && (
          <p
            className={classNames(
              'mt-spacing/gap/y-sm font-font-weight/caption text-text/secondary-lighter text-font-size/caption leading-line-height/caption',
              captionAlignmentClass,
            )}
          >
            {caption}
          </p>
        )}
      </div>
    </TheWrapper>
  );
};
